import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import ChangePasswordForm from '../components/ChangePasswordForm';
import EmailLink from '../components/EmailLink';
import { InvoiceViewModal } from '../components/InvoiceViewModal';
import PhoneLink from '../components/PhoneLink';
import { floorService, invoiceService, roomService, tenantService } from '../services/api';
import { Floor, Invoice, Room, User } from '../types';
import './Dashboard.css';
import './ViewTenantUser.css';

function slugPart(v: string | number | undefined | null): string {
  const s = String(v ?? 'na')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 48);
  return s || 'na';
}

function formatFloorRoomBed(t: User, floorById: Record<number, Floor>, roomById: Record<number, Room>): string {
  const fid = t.floor != null && String(t.floor).trim() !== '' ? Number(t.floor) : NaN;
  const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;
  const fLabel =
    Number.isFinite(fid) && floorById[fid] != null ? String(floorById[fid].floorNumber) : String(t.floor ?? '').trim() || '—';
  const rLabel =
    Number.isFinite(rid) && roomById[rid] != null ? String(roomById[rid].roomNumber) : String(t.room ?? '').trim() || '—';
  const b = String(t.bed ?? '').trim() || '—';
  if (fLabel === '—' && rLabel === '—' && b === '—') return '—';
  return `${fLabel}-${rLabel}-${b}`;
}

function dateDisplay(v: string | undefined | null): string {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatAdvance(t: User): string {
  const a = t.advance;
  if (a === undefined || a === null || a === '') return '—';
  const n = Number(a);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : String(a);
}

function paymentTypeLabel(t: Invoice['paymentType']): string {
  switch (t) {
    case 'RENT':
      return 'Rent';
    case 'ADVANCE':
      return 'Advance';
    case 'DEPOSIT':
      return 'Deposit';
    case 'REFUND':
      return 'Refund';
    default:
      return String(t || '—');
  }
}

function proofUrlKind(url: string): 'image' | 'pdf' | 'other' {
  const path = url.split(/[?#]/)[0]?.toLowerCase() ?? '';
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/.test(path)) return 'image';
  if (/\.pdf$/i.test(path)) return 'pdf';
  return 'other';
}

async function downloadRemoteFile(url: string, filenameWithoutExt: string) {
  const extMatch = url.match(/\.(jpe?g|png|gif|webp|pdf)(\?|$)/i);
  const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
  const fullName = `${filenameWithoutExt}.${ext}`;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('bad status');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fullName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    toast.success('Download started');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info('Opened in a new tab. Use Save as to keep a copy if the download did not start.');
  }
}

const ViewTenantUser: React.FC = () => {
  const { hostel } = useAuth();
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const userId = userIdParam != null && userIdParam !== '' ? parseInt(userIdParam, 10) : NaN;

  const [tenant, setTenant] = useState<User | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [idProofPreview, setIdProofPreview] = useState<{ url: string; label: string; key: string } | null>(null);
  const [idProofPreviewImgErr, setIdProofPreviewImgErr] = useState(false);
  const [invoiceView, setInvoiceView] = useState<Invoice | null>(null);
  const [invoiceEdit, setInvoiceEdit] = useState<Invoice | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    paidAmount: '',
    balanceAmount: '',
    discount: '',
    dueDate: '',
    note: '',
  });

  const floorById = useMemo(
    () => Object.fromEntries(floors.map((f) => [f.id, f])) as Record<number, Floor>,
    [floors]
  );
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])) as Record<number, Room>, [rooms]);

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const ta = new Date(a.paymentTimestamp || 0).getTime();
      const tb = new Date(b.paymentTimestamp || 0).getTime();
      return tb - ta;
    });
  }, [invoices]);

  useEffect(() => {
    if (!hostel?.id) return;
    if (!Number.isFinite(userId)) {
      toast.error('Invalid link');
      navigate('/tenants');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, fl, rm, inv] = await Promise.all([
          tenantService.getHostelTenant(hostel.id, userId),
          floorService.getFloorsByHostel(hostel.id),
          roomService.getRoomsByHostel(hostel.id),
          invoiceService.getUserInvoices(userId).catch(() => [] as Invoice[]),
        ]);
        if (cancelled) return;
        setTenant(t);
        setFloors(fl.filter((f) => f.isActive !== false));
        setRooms(rm.filter((r) => r.isActive !== false));
        setInvoices(Array.isArray(inv) ? inv : []);
      } catch {
        if (!cancelled) {
          toast.error('Could not load user');
          navigate('/tenants');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hostel?.id, userId, navigate]);

  useEffect(() => {
    document.body.classList.add('vtv-print-profile');
    return () => {
      document.body.classList.remove('vtv-print-profile');
    };
  }, []);

  useEffect(() => {
    setIdProofPreviewImgErr(false);
  }, [idProofPreview?.url]);

  useEffect(() => {
    if (!idProofPreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIdProofPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idProofPreview]);

  const reloadInvoices = async () => {
    if (!Number.isFinite(userId)) return;
    try {
      const inv = await invoiceService.getUserInvoices(userId);
      setInvoices(Array.isArray(inv) ? inv : []);
    } catch {
      toast.error('Could not refresh invoices');
    }
  };

  const govIdItems = useMemo(() => {
    if (!tenant) return [];
    const items: { key: string; label: string; url: string; suffix: string }[] = [];
    if (tenant.idFrontSide) items.push({ key: 'front', label: 'ID front', url: tenant.idFrontSide, suffix: 'gov-id-front' });
    if (tenant.idBackSide) items.push({ key: 'back', label: 'ID back', url: tenant.idBackSide, suffix: 'gov-id-back' });
    return items;
  }, [tenant]);

  const handleDownload = (url: string, suffix: string) => {
    if (!tenant) return;
    const ts = Date.now();
    const base = [slugPart(tenant.floor), slugPart(tenant.room), slugPart(tenant.bed), slugPart(tenant.username), String(ts)].join('-');
    void downloadRemoteFile(url, `${base}-${suffix}`);
  };

  const handleDownloadInvoicePdf = async (inv: Invoice) => {
    try {
      const blob = await invoiceService.downloadInvoice(inv.id, inv.userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.invoiceNumber || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice PDF downloaded');
    } catch {
      toast.error('Failed to download invoice PDF');
    }
  };

  const toDateInputValue = (raw: unknown): string => {
    if (raw == null || raw === '') return '';
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const openEditInvoice = (inv: Invoice) => {
    setInvoiceEdit(inv);
    setEditForm({
      paidAmount: inv.paidAmount != null ? String(inv.paidAmount) : '0',
      balanceAmount: inv.balanceAmount != null ? String(inv.balanceAmount) : '0',
      discount: inv.discount != null ? String(inv.discount) : '0',
      dueDate: toDateInputValue(inv.dueDate),
      note: inv.note != null ? String(inv.note) : '',
    });
  };

  const handleSaveInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoiceEdit) return;

    const paidAmount = Number(editForm.paidAmount);
    const balanceAmount = Number(editForm.balanceAmount);
    const discount = Number(editForm.discount);
    if (!Number.isFinite(paidAmount) || paidAmount < 0) {
      toast.error('Paid amount must be a valid number ≥ 0');
      return;
    }
    if (!Number.isFinite(balanceAmount) || balanceAmount < 0) {
      toast.error('Balance must be a valid number ≥ 0');
      return;
    }
    if (!Number.isFinite(discount) || discount < 0) {
      toast.error('Discount must be a valid number ≥ 0');
      return;
    }

    setEditSaving(true);
    try {
      await invoiceService.updateInvoice(invoiceEdit.id, {
        paidAmount: String(paidAmount),
        balanceAmount: String(balanceAmount),
        discount: String(discount),
        dueDate: editForm.dueDate || undefined,
        note: editForm.note,
      });
      toast.success('Invoice updated');
      setInvoiceEdit(null);
      await reloadInvoices();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update invoice');
    } finally {
      setEditSaving(false);
    }
  };

  if (!hostel) return <div className="loading-container">Loading…</div>;
  if (!Number.isFinite(userId)) return <div className="loading-container">Redirecting…</div>;
  if (loading) return <div className="loading-container vtv-loading">Loading profile…</div>;
  if (!tenant) return null;

  const kycDone = Boolean(tenant.idFrontSide && tenant.idBackSide);
  const locationLine = formatFloorRoomBed(tenant, floorById, roomById);
  const addressTrim = tenant.address?.trim() || '';
  const mapsHref =
    addressTrim !== ''
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressTrim)}`
      : null;

  return (
    <div className="dashboard-container vtv-page">
      <header className="vtv-topbar vtv-screen-only">
        <button type="button" className="vtv-back" onClick={() => navigate('/tenants')}>
          ← Users
        </button>
        <div className="vtv-top-actions">
          <button
            type="button"
            className="vtv-btn"
            title="Print preview omits transactions and important note"
            onClick={() => window.print()}
          >
            Print profile
          </button>
          <button type="button" className="vtv-btn" onClick={() => navigate(`/tenants/edit/${tenant.id}`)}>
            Edit user
          </button>
          <button type="button" className="vtv-btn" onClick={() => setPasswordModalOpen(true)}>
            Change password
          </button>
        </div>
      </header>

      <div className="vtv-layout">
        <aside className="vtv-sidebar">
          <div className="vtv-card vtv-profile-card">
            <div className="vtv-avatar-wrap">
              {tenant.photo ? (
                <img src={tenant.photo} alt="" className="vtv-avatar" />
              ) : (
                <div className="vtv-avatar vtv-avatar--placeholder" aria-hidden>
                  {(tenant.username || '?').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <h1 className="vtv-profile-name">{tenant.username || '—'}</h1>
            <p className={`vtv-status ${tenant.isActive ? 'vtv-status--active' : 'vtv-status--inactive'}`}>
              {tenant.isActive ? 'Active' : 'Inactive'}
            </p>
            <dl className="vtv-quick-dl">
              <div>
                <dt>Phone</dt>
                <dd>
                  <PhoneLink phone={tenant.phoneNumber} className="vtv-link" />
                </dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  <EmailLink email={tenant.email} className="vtv-link" />
                </dd>
              </div>
              <div>
                <dt>Room allocation</dt>
                <dd>{locationLine}</dd>
              </div>
              {addressTrim !== '' ? (
                <div>
                  <dt>Address / location</dt>
                  <dd>
                    <span className="vtv-address-text">{addressTrim}</span>
                    {mapsHref ? (
                      <a className="vtv-maps-link" href={mapsHref} target="_blank" rel="noopener noreferrer">
                        Open in Maps
                      </a>
                    ) : null}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt>Joined</dt>
                <dd>{dateDisplay(tenant.dateOfJoining)}</dd>
              </div>
            </dl>
            <div className="vtv-tags" aria-label="Tags">
              <span className="vtv-tag">Tenant</span>
              {kycDone ? <span className="vtv-tag vtv-tag--ok">KYC complete</span> : <span className="vtv-tag vtv-tag--warn">KYC pending</span>}
              {tenant.isNotice ? <span className="vtv-tag vtv-tag--alert">On notice</span> : null}
            </div>
          </div>

          {govIdItems.length > 0 ? (
            <div className="vtv-card vtv-id-docs-card">
              <h2 className="vtv-id-docs-title">Government ID</h2>
              <div className="vtv-id-docs-grid">
                {govIdItems.map((item) => {
                  const kind = proofUrlKind(item.url);
                  const openPreview = () => setIdProofPreview({ url: item.url, label: item.label, key: item.key });
                  return (
                    <div key={item.key} className="vtv-id-doc-cell">
                      <span className="vtv-id-doc-label">{item.label}</span>
                      {kind === 'image' ? (
                        <button
                          type="button"
                          className="vtv-id-doc-media-btn vtv-id-doc-media-btn--image"
                          onClick={openPreview}
                          aria-label={`Preview ${item.label}`}
                        >
                          <img src={item.url} alt="" className="vtv-id-doc-image" loading="lazy" decoding="async" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`vtv-id-doc-media-btn vtv-id-doc-media-btn--${kind === 'pdf' ? 'pdf' : 'file'}`}
                          onClick={openPreview}
                          aria-label={`Preview ${item.label}`}
                        >
                          <span className="vtv-id-doc-badge">{kind === 'pdf' ? 'PDF' : 'File'}</span>
                          <span className="vtv-id-doc-hint">Click to preview</span>
                        </button>
                      )}
                      <button type="button" className="vtv-id-doc-download" onClick={() => handleDownload(item.url, item.suffix)}>
                        Download
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </aside>

        <main className="vtv-main">
          <section className="vtv-card vtv-section-card">
            <h2 className="vtv-section-heading">Personal details</h2>
            <div className="vtv-field-grid">
              <div className="vtv-field">
                <span className="vtv-field-label">Full name</span>
                <span className="vtv-field-value">{tenant.username || '—'}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Phone</span>
                <span className="vtv-field-value">
                  <PhoneLink phone={tenant.phoneNumber} className="vtv-link" />
                </span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Email</span>
                <span className="vtv-field-value">
                  <EmailLink email={tenant.email} className="vtv-link" />
                </span>
              </div>
              <div className="vtv-field vtv-field--full">
                <span className="vtv-field-label">Address</span>
                <span className="vtv-field-value">{addressTrim || '—'}</span>
              </div>
              <div className="vtv-field vtv-field--full">
                <span className="vtv-field-label">Working place</span>
                <span className="vtv-field-value">{tenant.workingPlace?.trim() ? tenant.workingPlace : '—'}</span>
              </div>
            </div>

            <h2 className="vtv-section-heading vtv-section-heading--sub">Emergency contact</h2>
            <div className="vtv-field-grid">
              <div className="vtv-field">
                <span className="vtv-field-label">Name</span>
                <span className="vtv-field-value">{tenant.emergencyContactName?.trim() ? tenant.emergencyContactName : '—'}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Phone</span>
                <span className="vtv-field-value">
                  <PhoneLink phone={tenant.emergencyContactPhoneNumber} className="vtv-link" />
                </span>
              </div>
            </div>
          </section>

          <section className="vtv-card vtv-section-card">
            <h2 className="vtv-section-heading">Stay details</h2>
            <div className="vtv-field-grid">
              <div className="vtv-field">
                <span className="vtv-field-label">Floor – room – bed</span>
                <span className="vtv-field-value">{locationLine}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Rent</span>
                <span className="vtv-field-value">{tenant.rent != null && tenant.rent !== '' ? tenant.rent : '—'}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Advance</span>
                <span className="vtv-field-value">{formatAdvance(tenant)}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Date of joining</span>
                <span className="vtv-field-value">{dateDisplay(tenant.dateOfJoining)}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Payment date</span>
                <span className="vtv-field-value">{dateDisplay(tenant.paymentDate)}</span>
              </div>
              <div className="vtv-field">
                <span className="vtv-field-label">Vacated date</span>
                <span className="vtv-field-value">{dateDisplay(tenant.dateOfLeft)}</span>
              </div>
            </div>
          </section>

          <section className="vtv-card vtv-note-card vtv-no-print">
            <h2 className="vtv-note-title">Important note</h2>
            <div className="vtv-note-body">{tenant.note?.trim() ? tenant.note : '—'}</div>
          </section>

          <section className="vtv-card vtv-section-card vtv-no-print">
            <div className="vtv-section-head-row">
              <h2 className="vtv-section-heading vtv-section-heading--flush">Transactions</h2>
              <span className="vtv-section-hint">Invoices for this user in this hostel</span>
            </div>
            {sortedInvoices.length === 0 ? (
              <p className="vtv-muted vtv-muted--block">No invoices yet.</p>
            ) : (
              <div className="vtv-table-wrap responsive-table-wrap">
                <table className="vtv-transactions-table responsive-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Period</th>
                      <th>Type</th>
                      <th>Paid</th>
                      <th>Discount</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th className="vtv-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedInvoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="vtv-mono responsive-table__primary" data-label="Invoice">#{inv.invoiceNumber || inv.id}</td>
                        <td data-label="Period">
                          {inv.paymentMonth || '—'} {inv.paymentYear || ''}
                        </td>
                        <td data-label="Type">{paymentTypeLabel(inv.paymentType)}</td>
                        <td data-label="Paid">{inv.paidAmount ?? '—'}</td>
                        <td data-label="Discount">{inv.discount ?? '0'}</td>
                        <td data-label="Balance">{inv.balanceAmount ?? '—'}</td>
                        <td data-label="Status">{inv.paymentStatus?.trim() ? inv.paymentStatus : '—'}</td>
                        <td data-label="Date">{dateDisplay(inv.paymentTimestamp)}</td>
                        <td className="responsive-table__actions" data-label="Actions">
                          <div className="vtv-row-actions">
                            <button type="button" className="vtv-mini-btn" onClick={() => setInvoiceView(inv)}>
                              View
                            </button>
                            <button type="button" className="vtv-mini-btn vtv-mini-btn--accent" onClick={() => openEditInvoice(inv)}>
                              Edit
                            </button>
                            <button type="button" className="vtv-mini-btn vtv-mini-btn--primary" onClick={() => handleDownloadInvoicePdf(inv)}>
                              PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

        </main>
      </div>

      {idProofPreview ? (
        <div className="vtv-modal-overlay" role="presentation" onClick={() => setIdProofPreview(null)}>
          <div
            className="vtv-modal-card vtv-proof-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vtv-proof-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vtv-modal-header">
              <h3 id="vtv-proof-preview-title">{idProofPreview.label}</h3>
              <button type="button" className="vtv-modal-close" aria-label="Close" onClick={() => setIdProofPreview(null)}>
                ×
              </button>
            </div>
            <div className="vtv-proof-preview-body">
              {proofUrlKind(idProofPreview.url) === 'pdf' ? (
                <>
                  <p className="vtv-proof-pdf-hint">If the preview is empty (some hosts block embedding), open the PDF in a new tab.</p>
                  <iframe className="vtv-proof-preview-frame" src={idProofPreview.url} title={idProofPreview.label} />
                  <div className="vtv-proof-preview-links">
                    <a className="vtv-link" href={idProofPreview.url} target="_blank" rel="noopener noreferrer">
                      Open PDF in new tab
                    </a>
                  </div>
                </>
              ) : !idProofPreviewImgErr ? (
                <img
                  src={idProofPreview.url}
                  alt=""
                  className="vtv-proof-preview-img"
                  onError={() => setIdProofPreviewImgErr(true)}
                />
              ) : (
                <p className="vtv-muted vtv-proof-fallback">
                  This file cannot be shown inline.{' '}
                  <a className="vtv-link" href={idProofPreview.url} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {passwordModalOpen && hostel ? (
        <div className="vtv-modal-overlay" role="presentation" onClick={() => setPasswordModalOpen(false)}>
          <div
            className="vtv-modal-card vtv-password-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vtv-password-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vtv-modal-header">
              <h3 id="vtv-password-modal-title">Change tenant password</h3>
              <button
                type="button"
                className="vtv-modal-close"
                aria-label="Close"
                onClick={() => setPasswordModalOpen(false)}
              >
                ×
              </button>
            </div>
            <ChangePasswordForm
              userId={tenant.id}
              variant="modal"
              hint={`Set a new sign-in password for ${tenant.username || 'this tenant'}. They will use it with their phone number to log in.`}
              submitPassword={(password) => tenantService.resetTenantPassword(hostel.id, tenant.id, password)}
              onSuccess={() => setPasswordModalOpen(false)}
              onCancel={() => setPasswordModalOpen(false)}
            />
          </div>
        </div>
      ) : null}

      {invoiceView ? (
        <InvoiceViewModal
          invoice={invoiceView}
          tenant={tenant}
          hostel={hostel}
          locationLookup={{ floorById, roomById }}
          onClose={() => setInvoiceView(null)}
          onDownload={handleDownloadInvoicePdf}
        />
      ) : null}

      {invoiceEdit ? (
        <div className="vtv-modal-overlay" role="presentation" onClick={() => !editSaving && setInvoiceEdit(null)}>
          <div className="vtv-modal-card" role="dialog" aria-labelledby="vtv-inv-edit-title" onClick={(e) => e.stopPropagation()}>
            <div className="vtv-modal-header">
              <h3 id="vtv-inv-edit-title">Edit invoice #{invoiceEdit.invoiceNumber || invoiceEdit.id}</h3>
              <button type="button" className="vtv-modal-close" aria-label="Close" disabled={editSaving} onClick={() => setInvoiceEdit(null)}>
                ×
              </button>
            </div>
            <p className="vtv-modal-warn">Saving may trigger your server&apos;s invoice notifications (SMS / email), if configured.</p>
            <form onSubmit={handleSaveInvoice} className="vtv-edit-form">
              <label className="vtv-edit-field">
                <span>Paid amount</span>
                <input
                  className="vtv-edit-input"
                  value={editForm.paidAmount}
                  onChange={(e) => setEditForm((f) => ({ ...f, paidAmount: e.target.value }))}
                />
              </label>
              <label className="vtv-edit-field">
                <span>Balance</span>
                <input
                  className="vtv-edit-input"
                  value={editForm.balanceAmount}
                  onChange={(e) => setEditForm((f) => ({ ...f, balanceAmount: e.target.value }))}
                />
              </label>
              <label className="vtv-edit-field">
                <span>Discount</span>
                <input
                  className="vtv-edit-input"
                  value={editForm.discount}
                  onChange={(e) => setEditForm((f) => ({ ...f, discount: e.target.value }))}
                />
              </label>
              <label className="vtv-edit-field">
                <span>Due date</span>
                <input
                  type="date"
                  className="vtv-edit-input"
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
              <label className="vtv-edit-field vtv-edit-field--full">
                <span>Note</span>
                <textarea
                  className="vtv-edit-textarea"
                  rows={3}
                  value={editForm.note}
                  onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                />
              </label>
              <div className="vtv-modal-footer">
                <button type="button" className="vtv-btn vtv-btn--secondary" disabled={editSaving} onClick={() => setInvoiceEdit(null)}>
                  Cancel
                </button>
                <button type="submit" className="vtv-btn vtv-btn--primary" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ViewTenantUser;
